import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AIIconGenerator } from '@/components/AIIconGenerator';
import { SupabaseIconGallery } from '@/components/SupabaseIconGallery';
import { IconUploadManager } from '@/components/IconUploadManager';
import { ReferenceImageUploader } from '@/components/ReferenceImageUploader';
import { Palette, Sparkles, Upload, Image, Settings } from 'lucide-react';

export const IconsHub: React.FC = () => {
  const [activeTab, setActiveTab] = useState('gallery');
  const [referenceImages, setReferenceImages] = useState<File[]>([]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="p-3 bg-blue-100 rounded-full">
              <Palette className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-4xl font-bold text-cyan-600">
              Icons Hub
            </h1>
          </div>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Comprehensive icon management with AI generation, upload, and gallery features
          </p>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-8 bg-white/80 backdrop-blur-sm border border-blue-200">
            <TabsTrigger 
              value="gallery" 
              className="flex items-center gap-2 data-[state=active]:bg-blue-500 data-[state=active]:text-white"
            >
              <Image className="w-4 h-4" />
              Gallery
            </TabsTrigger>
            <TabsTrigger 
              value="ai-generator" 
              className="flex items-center gap-2 data-[state=active]:bg-blue-500 data-[state=active]:text-white"
            >
              <Sparkles className="w-4 h-4" />
              AI Generator
            </TabsTrigger>
            <TabsTrigger 
              value="upload" 
              className="flex items-center gap-2 data-[state=active]:bg-blue-500 data-[state=active]:text-white"
            >
              <Upload className="w-4 h-4" />
              Upload
            </TabsTrigger>
            <TabsTrigger 
              value="settings" 
              className="flex items-center gap-2 data-[state=active]:bg-blue-500 data-[state=active]:text-white"
            >
              <Settings className="w-4 h-4" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Gallery Tab */}
          <TabsContent value="gallery" className="space-y-6">
            <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-blue-200 p-1">
              <SupabaseIconGallery />
            </div>
          </TabsContent>

          {/* AI Generator Tab */}
          <TabsContent value="ai-generator" className="space-y-6">
            <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-blue-200 p-6">
              {/* Reference Images Section */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Palette className="w-5 h-5 text-blue-600" />
                  Character Consistency References
                </h3>
                <div className="bg-blue-50 rounded-lg p-4 mb-4">
                  <ReferenceImageUploader
                    onImagesChange={setReferenceImages}
                    maxImages={3}
                  />
                </div>
                {referenceImages.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-700">
                      ✅ <strong>{referenceImages.length} reference images loaded</strong> - 
                      These will be used to ensure character consistency across all generated icons.
                    </p>
                  </div>
                )}
              </div>

              {/* AI Generator */}
              <AIIconGenerator 
                onIconGenerated={(filename, publicUrl) => {
                  console.log('Icon generated:', filename, publicUrl);
                }}
                onError={(error) => {
                  console.error('Generation error:', error);
                }}
              />
            </div>
          </TabsContent>

          {/* Upload Tab */}
          <TabsContent value="upload" className="space-y-6">
            <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-blue-200 p-1">
              <IconUploadManager />
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-blue-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-6">Icon Management Settings</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* API Configuration */}
                <div className="p-4 border border-gray-200 rounded-lg">
                  <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    API Configuration
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Service:</span>
                      <span className="font-medium text-green-600">Fal.ai (Active)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Model:</span>
                      <span className="font-medium">Gemini 2.5 Flash Image</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Cost per Icon:</span>
                      <span className="font-medium">$0.039</span>
                    </div>
                  </div>
                </div>

                {/* Storage Configuration */}
                <div className="p-4 border border-gray-200 rounded-lg">
                  <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Storage Configuration
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Backend:</span>
                      <span className="font-medium text-blue-600">Supabase Storage</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Bucket:</span>
                      <span className="font-medium">app-icons</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Max File Size:</span>
                      <span className="font-medium">5MB</span>
                    </div>
                  </div>
                </div>

                {/* Character Consistency */}
                <div className="p-4 border border-gray-200 rounded-lg">
                  <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Palette className="w-4 h-4" />
                    Character Consistency
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Style:</span>
                      <span className="font-medium">Minimalist Line Art</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Colors:</span>
                      <span className="font-medium">#3B82F6 → #06B6D4</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Reference Support:</span>
                      <span className="font-medium text-green-600">Enabled</span>
                    </div>
                  </div>
                </div>

                {/* Usage Statistics */}
                <div className="p-4 border border-gray-200 rounded-lg">
                  <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Usage Statistics
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Icons Generated:</span>
                      <span className="font-medium">-</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Cost:</span>
                      <span className="font-medium">$0.00</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Success Rate:</span>
                      <span className="font-medium">-</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Status Information */}
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-800 mb-2">System Status</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>Fal.ai API: Online</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>Supabase Storage: Online</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span>Reference Images: Supported</span>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default IconsHub;